import './patientInformationEditComponent.scss'
import React, {useCallback, useEffect, useRef, useState} from 'react';
import * as Yup from "yup";
import Grid from "@mui/material/Grid";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import FormikInputComponent from "../../../../../shared/components/core/formik-input/FormikInputComponent";
import FormikDatePickerComponent
    from "../../../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import formValidationRules from "../../../../../shared/services/validation.service";
import FormikSelectDropdownComponent
    from "../../../../../shared/components/core/formik-select-dropdown/FormikSelectDropdownComponent";
import CommonService from "../../../../../shared/services/common.service";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import FormikAutoCompleteDropdown
    from "../../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import {APIConfig} from "../../../../../constants";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import _ from "lodash";
import FromikPhoneNumberComponent
    from "../../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";
import PatientEditConfirmationComponent from "../../patient-edit-confirmation/PatientEditConfirmationComponent";
import { useSelector } from 'react-redux';
import { IRootReducerState } from '../../../../../store/reducers';

interface PatientInformationEditComponentProps {
    patientDetails: any;
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const patientBasicInformationFormValidationSchema = Yup.object({
    reasonForEdit: Yup.string(),
    prefix: Yup.string().nullable(),
    suffix: Yup.string().nullable(),
    firstName: Yup.string()
        .required("First Name  is required")
        .max(100, 'First name cannot exceed 100 characters'),
    middleName: Yup.string().nullable()
        .max(100, 'Middle name cannot exceed 100 characters'),
    lastName: Yup.string()
        .required("Last Name  is required")
        .max(100, 'Last name cannot exceed 100 characters'),
    businessEmailId: Yup.string().nullable().email("Invalid Business Email ID"),
    businessMobileNumber: Yup.string().nullable().matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    mobileNumber: Yup.string()
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits")
        .required("Phone Number  is required"),
    secondaryMobileNumber: Yup.string().nullable()
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    dateOfBirth: Yup.date()
        .required("Date of birth  is required")
        .typeError("Please Enter valid Date")
        .min(new Date(1900, 0, 1), "Date of birth should be greater than 01/01/1900")
        .max(new Date(), "Can not be in the future")
        .nullable(),
    emailId: Yup.string()
        .nullable()
        .email("Invalid Email ID")
        .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Email ID"),
    gender: Yup.string().required("Gender is required").nullable(),
    weight: Yup.number()
        .min(0, "Weight should be greater than 0")
        .max(1400, 'Max weight can be 1400 lbs')
        .nullable(),
    heightInInches: Yup.string().nullable(),
    heightInFeet: Yup.string().nullable(),
    addressLine1: Yup.string().nullable()
        .required("Address 1 is required")
        .max(200, 'Address Line 1 cannot exceed 200 characters'),
    addressLine2: Yup.string().nullable()
        .max(200, 'Address Line 2 cannot exceed 200 characters'),
    zipcode: Yup.mixed().required("Zip Code is required").nullable(),
    race: Yup.string().required('Race is Required').nullable(),
    ethnicity: Yup.string().required('Ethnicity is Required').nullable(),
    state: Yup.mixed().required("State is required").nullable(),
    city: Yup.mixed().required("City is required").nullable(),
    county: Yup.mixed()
        .required("County is required")
        .nullable(),
    country: Yup.mixed()
        .required("Country is required")
        .nullable(),
    newZipCode: Yup.string().when('zipcode', {
        is: (zipcode: any) => (zipcode?.zipcode && zipcode?.zipcode?.toLowerCase() === "other"),
        then: Yup.string().required('Zip Code is required').matches(/\d+/, 'only digits allowed').max(5, "Zipcode should be 5 digits").min(5, "Zipcode should be 5 digits").nullable(),
    }),
});

interface PatientBasicInformationFormSchema {
    prefix: string,
    suffix: string,
    reasonForEdit: string;
    firstName: string,
    middleName: string,
    lastName: string,
    dateOfBirth: string,
    gender: string,
    race: string,
    ethnicity: string,
    businessEmailId: string,
    businessMobileNumber: string,
    mobileNumber: string,
    secondaryMobileNumber: string,
    emailId: string,
    addressLine1: string,
    addressLine2: string,
    zipcode: any,
    newZipCode: string,
    state: string,
    city: string,
    county: string,
    country: string,
    heightInFeet: string;
    heightInInches: string;
}

const patientDetailsFormInitialValues: PatientBasicInformationFormSchema = {
    reasonForEdit: '',
    prefix: "",
    suffix: "",
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: "",
    businessEmailId: "",
    businessMobileNumber: "",
    mobileNumber: "",
    secondaryMobileNumber: "",
    gender: "",
    race: "",
    ethnicity: "",
    emailId: "",
    addressLine1: "",
    addressLine2: "",
    zipcode: {},
    newZipCode: "",
    state: "",
    city: "",
    county: "",
    country: "",
    heightInFeet: "",
    heightInInches: ""
}


const PatientInformationEditComponent = (props: PatientInformationEditComponentProps) => {
    const {patientDetails, onUpdateComplete, onEditCancel} = props;
    const {prefixList, suffixList} = useSelector((state: IRootReducerState) => state.staticData);
    const formikRef = useRef<any>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const [patientInformationInitialValues, setPatientInformationInitialValues] = useState<PatientBasicInformationFormSchema>(_.cloneDeep(patientDetailsFormInitialValues));
    const [raceList, setRaceList] = useState<any>([]);
    const [isZipCodeNotFound, setIsZipCodeNotFound] = useState<boolean>(false);
    const [state, setState] = useState<string>("");

    const [isRaceListLoading, setIsRaceListLoading] = useState<any>([]);
    const [isRaceListLoadingFailed, setIsRaceListLoadingFailed] = useState<any>([]);
    const [ethnicityList, setEthnicityList] = useState<any>([]);
    const [isEthnicityListLoading, setIsEthnicityListLoading] = useState<any>([]);
    const [isEthnicityListLoadingFailed, setIsEthnicityListLoadingFailed] = useState<any>([]);

    const getRaceList = useCallback(() => {
        const payload = {};
        setIsRaceListLoading(true);
        setIsRaceListLoadingFailed(false);
        let raceList: any = [];
        CommonService._staticData.getRaceList(payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    raceList = response.data;
                }
                setRaceList(raceList);
                setIsRaceListLoading(false);
                setIsRaceListLoadingFailed(false);
            })
            .catch((error: any) => {
                setRaceList(raceList);
                setIsRaceListLoading(false);
                setIsRaceListLoadingFailed(true);
            });
    }, []);
    const getEthnicityList = useCallback(() => {
        const payload = {};
        setIsEthnicityListLoading(true);
        setIsEthnicityListLoadingFailed(false);
        let ethnicityList: any = [];
        CommonService._staticData.getEthnicityList(payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    ethnicityList = response.data;
                }
                setEthnicityList(ethnicityList);
                setIsEthnicityListLoading(false);
                setIsEthnicityListLoadingFailed(false);
            })
            .catch((error: any) => {
                setEthnicityList(ethnicityList);
                setIsEthnicityListLoading(false);
                setIsEthnicityListLoadingFailed(true);
            });
    }, []);

    useEffect(() => {
        getEthnicityList();
        getRaceList();
    }, [getEthnicityList, getRaceList]);


    useEffect(() => {
        if (patientDetails) {
            const patientDetailsObj = _.cloneDeep(patientDetails);
            if (!patientDetails.zipcodeDetails) {
                patientDetailsObj.newZipCode = patientDetailsObj?.zipcode;
                patientDetailsObj.zipcode = {
                    zipcode: "Other"
                };
                patientDetailsObj.state = patientDetailsObj?.stateDetails;
                patientDetailsObj.city = patientDetailsObj?.cityDetails;
                setIsZipCodeNotFound(true);
            } else {
                patientDetailsObj.zipcode = patientDetailsObj.zipcodeDetails;
                setIsZipCodeNotFound(false);
            }
            patientDetailsObj.mobileNumber = CommonService.getMaskedMobileNumber(patientDetailsObj.mobileNumber);
            patientDetailsObj.secondaryMobileNumber = CommonService.getMaskedMobileNumber(patientDetailsObj.secondaryMobileNumber);
            patientDetailsObj.businessMobileNumber = CommonService.getMaskedMobileNumber(patientDetailsObj.businessMobileNumber);
            setState(patientDetailsObj.state);
            setPatientInformationInitialValues(_.cloneDeep(patientDetailsObj));
        }
    }, [patientDetails]);


    const handleZipCodeValueChange = useCallback((zipCodeObject: any, setFieldValue: any) => {
        if (zipCodeObject === "") {
            setFieldValue("state", "");
            setFieldValue("city", "");
            setFieldValue("county", "");
            setFieldValue("country", "");
            setIsZipCodeNotFound(false);
            setState("");
        } else {
            if (zipCodeObject?.zipcode?.toLowerCase() === "other") {
                setIsZipCodeNotFound(true);
                setFieldValue("state", "");
                setFieldValue("city", "");
                setFieldValue("county", "");
                setFieldValue("country", "USA"); // set default country to USA
            } else {
                setIsZipCodeNotFound(false);
                setFieldValue("state", zipCodeObject.state);
                setFieldValue("city", zipCodeObject.city);
                setFieldValue("county", zipCodeObject.county);
                setFieldValue("country", zipCodeObject.country);
            }
        }
        setFieldValue("newZipCode", "");
    }, []);

    const handleDialogOpen = useCallback(() => {
        setIsDialogOpen(true);
        formikRef?.current.setFieldValue('reasonForEdit', '');
    }, []);

    const handleDialogClose = useCallback(() => {
        setIsDialogOpen(false);
        formikRef?.current.setFieldValue('reasonForEdit', '');
    }, []);

    const onFormSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
        const payload = {...values};
        payload.mobileNumber = payload.mobileNumber?.replace(/[^0-9]/g, "");
        payload.secondaryMobileNumber = payload.secondaryMobileNumber?.replace(/[^0-9]/g, "");
        payload.businessMobileNumber = payload.businessMobileNumber?.replace(/[^0-9]/g, "");
        payload['dateOfBirth'] = CommonService.convertDateFormat(payload['dateOfBirth'], 'YYYY-MM-DD')
        if (payload["newZipCode"]) {
            payload["zipcode"] = payload["newZipCode"];
            payload["state"] = payload?.state?.state;
            payload["city"] = payload?.city?.city;
            payload["county"] = payload?.county?.county;
            delete payload["newZipCode"];
        } else {
            payload["zipcode"] = payload?.zipcode?.zipcode;
        }
        setSubmitting(true);
        CommonService._patient.editPatientDetails(patientDetails?.id, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                CommonService._alert.showToast(response?.message || 'Patient Contact details has been added', "success");
                formikRef?.current.setFieldValue('reasonForEdit', '');
                onUpdateComplete(response.data);
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })
    }, [patientDetails, onUpdateComplete]);



    return (
        <div className="patient-information-component details-tab-data-content">

            <Formik
                validationSchema={patientBasicInformationFormValidationSchema}
                initialValues={patientInformationInitialValues}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
                validateOnMount={true}
                onSubmit={onFormSubmit}
                innerRef={formikRef}
            >
                {({isSubmitting, dirty, values, isValid, setFieldValue, validateForm, errors, submitForm}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values, errors]);
                    return (
                        <Form className={"patient-personal-information-holder"}>
                            <PatientEditConfirmationComponent
                                isOpen={isDialogOpen}
                                onSubmit={submitForm}
                                disabledNext={!values?.reasonForEdit}
                                isSubmitting={isSubmitting}
                                onClose={handleDialogClose}
                            />
                            <div className={'t-form-controls-wrapper'}>
                                <div className="form-sub-title">Personal Details</div>
                                <Grid container spacing={2}>
                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                <Field name={'prefix'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikSelectDropdownComponent
                                                                label={'Prefix'}
                                                                placeholder={'Enter Prefix'}
                                                                options={prefixList}
                                                                valueKey={"code"}
                                                                getOptionLabel={(option) => (option.title)}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                <Field name={'suffix'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikSelectDropdownComponent
                                                                label={'Suffix'}
                                                                placeholder={'Enter Suffix'}
                                                                options={suffixList}
                                                                valueKey={"code"}
                                                                getOptionLabel={(option) => (option.title)}
                                                                required={false}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'firstName'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'First Name'}
                                                        placeholder={'Enter First Name'}
                                                        type={"text"}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'middleName'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Middle Name'}
                                                        // readOnly={isPatientAlreadyExists === false || isPatientBelongsToFacility === false}
                                                        placeholder={'Enter Middle Name'}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'lastName'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Last Name'}
                                                        placeholder={'Enter Last Name'}
                                                        type={"text"}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'dateOfBirth'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'Date of Birth'}
                                                        formikField={field}
                                                        required={true}
                                                        maxDate={formValidationRules.today}
                                                        // readOnly={isPatientAlreadyExists === false || isPatientBelongsToFacility === false}
                                                        placeholder={'Enter Date of Birth'}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'gender'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikSelectDropdownComponent
                                                        label={'Gender'}
                                                        options={CommonService._staticData.genderOptions}
                                                        placeholder={'Select Gender'}
                                                        valueKey={'code'}
                                                        required={true}
                                                        getOptionLabel={(option) => (option.title)}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'ethnicity'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikSelectDropdownComponent
                                                        label={'Ethnicity'}
                                                        isDataLoading={isEthnicityListLoading}
                                                        isDataLoadingFailed={isEthnicityListLoadingFailed}
                                                        options={ethnicityList}
                                                        placeholder={'Select'}
                                                        formikField={field}
                                                        required={true}
                                                        getOptionLabel={(option) => (option.title)}
                                                        valueKey={"code"}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'race'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikSelectDropdownComponent
                                                        label={'Race'}
                                                        options={raceList}
                                                        placeholder={'Select Race'}
                                                        formikField={field}
                                                        isDataLoading={isRaceListLoading}
                                                        isDataLoadingFailed={isRaceListLoadingFailed}
                                                        getOptionLabel={(option) => (option.title)}
                                                        required={true}
                                                        valueKey={"code"}

                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'weight'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Weight (in lbs)'}
                                                        placeholder={'Enter Weight'}
                                                        type={"number"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'heightInFeet'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikSelectDropdownComponent
                                                        label={'Height (in feet)'}
                                                        required={false}
                                                        options={CommonService._staticData.heightInFeet}
                                                        placeholder={'Select Height in feet'}
                                                        formikField={field}
                                                        valueKey={'code'}
                                                        getOptionLabel={(option) => (option.title)}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'heightInInches'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikSelectDropdownComponent
                                                        label={'Height (in inches)'}
                                                        required={false}
                                                        options={CommonService._staticData.heightInInches}
                                                        placeholder={'Select Height in inches'}
                                                        valueKey={'code'}
                                                        formikField={field}
                                                        getOptionLabel={(option) => (option.title)}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <div className={'horizontal-line'}></div>
                                <div className="form-sub-title">Contact Details</div>
                                <Field name={'emailId'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                label={'Email ID'}
                                                placeholder={'Enter Email ID'}
                                                type={"email"}
                                                required={false}
                                                formikField={field}
                                            />
                                        )
                                    }
                                </Field>
                                <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'businessEmailId'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Business Email ID'}
                                                                placeholder={'Enter Business Email ID'}
                                                                type={"email"}
                                                                required={false}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'businessMobileNumber'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FromikPhoneNumberComponent
                                                                label={'Business Mobile Number'}
                                                                placeholder={'(000) 000-0000'}
                                                                maxLength={14}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'mobileNumber'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FromikPhoneNumberComponent
                                                        label={'Mobile Number'}
                                                        placeholder={'(000) 000-0000'}
                                                        maxLength={14}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'secondaryMobileNumber'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FromikPhoneNumberComponent
                                                        label={'Secondary Mobile Number'}
                                                        placeholder={'Secondary Mobile Number'}
                                                        maxLength={14}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <div className={'horizontal-line'}></div>
                                <div className="details-tab-data-header">
                                    <div className="view-and-edit-title"></div>
                                </div>
                                <div className="form-sub-title">Address Details</div>
                                <Field name={'addressLine1'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                label={'Address Line 1'}
                                                placeholder={'Enter Address Line 1'}
                                                type={"text"}
                                                required={true}
                                                formikField={field}
                                            />
                                        )
                                    }
                                </Field>
                                <Grid container spacing={2}>

                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'addressLine2'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Address Line 2'}
                                                        placeholder={'Enter Address Line 2'}
                                                        type={"text"}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'zipcode'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'ZIP Code'}
                                                        placeholder={'Start typing for ZIP Code'}
                                                        required={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                        openOnFocus={true}
                                                        url={APIConfig.ZIPCODE_LIST.URL}
                                                        method={APIConfig.ZIPCODE_LIST.METHOD}
                                                        searchMode={'serverSide'}
                                                        data={[]}
                                                        valueKey={'zipcode'}
                                                        dataListKey={"data.docs"}
                                                        onUpdate={(value: any) => {
                                                            handleZipCodeValueChange(value, setFieldValue)
                                                        }}
                                                        //Hiding other option from zipcode
                                                        // defaultData={[
                                                        //     {
                                                        //         zipcode: "Other"
                                                        //     }
                                                        // ]}
                                                        getOptionLabel={(option => (option.zipcode) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                {isZipCodeNotFound && <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'newZipCode'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            placeholder={"Enter New ZIP Code"}
                                                            label={'New ZIP Code'}
                                                            required={true}
                                                            maxLength={5}
                                                            type={"text"}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>

                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'state'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'State'}
                                                            required={true}
                                                            formikField={field}
                                                            url={APIConfig.STATE_LIST.URL}
                                                            method={APIConfig.STATE_LIST.METHOD}
                                                            searchMode={'serverSide'}
                                                            openOnFocus={true}
                                                            dataListKey={"data.docs"}
                                                            data={[]}
                                                            valueKey={'state'}
                                                            onUpdate={(value: any) => {
                                                                setState(value?.state);
                                                                field.form.setFieldValue("city", '');
                                                            }}
                                                            getOptionLabel={(option => (option.state) || '')}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'city'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'City'}
                                                            placeholder={'City'}
                                                            required={true}
                                                            valueKey={'city'}
                                                            id="city_auto_complete"
                                                            formikField={field}
                                                            searchMode={'serverSide'}
                                                            url={APIConfig.CITY_LIST.URL}
                                                            method={APIConfig.CITY_LIST.METHOD}
                                                            disabled={!state}
                                                            payload={{
                                                                state: state,
                                                                page: 1,
                                                                limit: 100
                                                            }}
                                                            data={[]}
                                                            getOptionLabel={(option => (option.city) || '')}
                                                            dataListKey={"data.docs"}
                                                        />
                                                    )
                                                }
                                            </Field>

                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            {/*<Field name={'timezone'}>*/}
                                            {/*    {*/}
                                            {/*        (field: FieldProps) => (*/}
                                            {/*            <FormikAutoCompleteDropdown*/}
                                            {/*                label={'Timezone'}*/}
                                            {/*                placeholder={'Timezone'}*/}
                                            {/*                required={true}*/}
                                            {/*                id="timezone_auto_complete"*/}
                                            {/*                formikField={field}*/}
                                            {/*                searchMode={'serverSide'}*/}
                                            {/*                url={APIConfig.CITY_LIST.URL}*/}
                                            {/*                method={APIConfig.CITY_LIST.METHOD}*/}
                                            {/*                disabled={!state}*/}
                                            {/*                payload={{*/}
                                            {/*                    state: state,*/}
                                            {/*                    page: 1,*/}
                                            {/*                    limit: 100*/}
                                            {/*                }}*/}
                                            {/*                data={[]}*/}
                                            {/*                getOptionLabel={(option => (option.city) || '')}*/}
                                            {/*                dataListKey={"data.docs"}*/}
                                            {/*            />*/}
                                            {/*        )*/}
                                            {/*    }*/}
                                            {/*</Field>*/}
                                            <Field name={'county'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikAutoCompleteDropdown
                                                                    label={'County'}
                                                                    placeholder={'County'}
                                                                    required={true}
                                                                    valueKey={'county'}
                                                                    id="county_auto_complete"
                                                                    formikField={field}
                                                                    searchMode={'serverSide'}
                                                                    url={APIConfig.COUNTY_LIST.URL}
                                                                    method={APIConfig.COUNTY_LIST.METHOD}
                                                                    disabled={!state}
                                                                    payload={{
                                                                        state: state,
                                                                        page: 1,
                                                                        limit: 100
                                                                    }}
                                                                    data={[]}
                                                                    getOptionLabel={(option => (option.county) || '')}
                                                                    dataListKey={"data.docs"}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Field name={'country'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    placeholder={"Enter Country"}
                                                                    label={'Country'}
                                                                    required={true}
                                                                    readOnly={true}
                                                                    type={"text"}
                                                                    formikField={field}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                            </Grid>
                                </>}
                                {
                                    !isZipCodeNotFound && values?.zipcode?.zipcode && values?.zipcode?.zipcode !== "Other" &&
                                    <>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'state'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'State'}
                                                                placeholder={'Enter state'}
                                                                required={true}
                                                                readOnly={true}
                                                                formikField={field}
                                                                type={"text"}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                            <Field name={'county'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'County'}
                                                                placeholder={'Enter county*'}
                                                                required={true}
                                                                readOnly={true}
                                                                formikField={field}
                                                                type={"text"}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                            <Field name={'city'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'City'}
                                                                placeholder={'Enter city*'}
                                                                required={true}
                                                                readOnly={true}
                                                                formikField={field}
                                                                type={"text"}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                            <Field name={'country'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Country'}
                                                                placeholder={'Enter country*'}
                                                                required={true}
                                                                readOnly={true}
                                                                formikField={field}
                                                                type={"text"}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    </>
                                }
                            </div>
                            <div className={"form-action-section-edit"}>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"secondary"}
                                    type={"button"}
                                    variant={"outlined"}
                                    disabled={isSubmitting}
                                    fullWidth={false}
                                    handleClick={() => {
                                        onEditCancel()
                                    }}
                                >
                                    Cancel
                                </ButtonComponent>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"primary"}
                                    // type={"submit"}
                                    variant={"contained"}
                                    disabled={!(isValid && dirty)}
                                    fullWidth={false}
                                    isLoading={isSubmitting}
                                    handleClick={handleDialogOpen}
                                >
                                    Save
                                </ButtonComponent>
                            </div>
                        </Form>
                    )
                }
                }
            </Formik>
        </div>
    )
}
export default PatientInformationEditComponent;
